import React, {
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import cn from 'classnames';

import {
	Checkbox,
	DynamicIconWrapper,
	Icons,
	PinAction,
	Select,
	SubmissionButtons,
	Tooltip,
} from '@components/common';

import { useOrganisationMapSettings } from '@hooks/organisation/useOrganisationMapSettings';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { auth, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import {
	resetClusterZoomLvl,
	resetRightSideControls,
	setBoroughsState,
	setCellIdsVisibility,
	setClusterZoomLevel,
	setIsShowSiteIds,
	setPinnedFilter,
} from '@slices/map';

import { getLocalStorage } from '@utils/storage';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { EControlsType, ESnackbarTypes, TEControlsType } from '@enums/common';
import { EDataType } from '@enums/map';

import { DEFAULT_CLUSTER_ZOOM_LVL, zoomLevelsOptions } from '@constants/common';
import { CLUSTER_ZOOM_LEVEL } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

import commonStyles from '@styles/common.module.scss';

import controlStyles from '../../MapControls.module.scss';
import styles from './MapSettings.module.scss';

const DEFAULT_CLUSTER_ZOOM_OPTION = {
	label: DEFAULT_CLUSTER_ZOOM_LVL,
	value: DEFAULT_CLUSTER_ZOOM_LVL,
};

const tooltipContent = 'Maximum zoom level at which clusters are generated. Setting value less than 16 may reduce' +
	' map performance.';

const MapSettings = () => {
	const {
		clusterZoomLvl,
		areBoroughsEnabled,
		showingCustomPolygonIds,
		markerType,
		isShowSiteIds,
		isCellIdsVisible,
		sectors: { isSectorsShown },
	} = useAppSelector(map);

	const {
		accessPermission: {
			networkOperatorOrganizationAdministrator,
			assetConsumerOrganizationAdministrator,
			assetOwnerOrganizationAdministrator,
		},
		activeOrganisation: organizationId,
	} = useAppSelector(auth);

	const isClusterZoomLvlAvailable = networkOperatorOrganizationAdministrator ||
		assetConsumerOrganizationAdministrator ||
		assetOwnerOrganizationAdministrator;

	const dispatch = useAppDispatch();

	const [selectedClusterZoomOption, setSelectedClusterZoomOption] = useState<TSelectOption>(() => ({
		value: clusterZoomLvl,
		label: clusterZoomLvl,
	}));
	const [areBoroughsEnabledLocalState, setAreBoroughsEnabledLocalState] = useState(areBoroughsEnabled);
	const [isDefaultClusterZoom, setIsDefaultClusterZoom] = useState(false);
	const [isApplyAvailable, setIsApplyAvailable] = useState(false);
	const [isPinned, setIsPinned] = useState(false);
	const [isSitesMode, setIsSitesMode] = useState(false);
	const [isShowSiteIdsLocalState, setIsShowSiteIdsLocalState] = useState(isShowSiteIds);
	const [isShowCellIdsLocalState, setIsShowCellIdsLocalState] = useState(isCellIdsVisible);

	const containerRef = useRef<HTMLDivElement>(null);

	const isClusterZoomLvlFromLocalStorage = getLocalStorage(CLUSTER_ZOOM_LEVEL);

	const { handleSetOrganisationMapSettings } = useOrganisationMapSettings();

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		setIsSitesMode(markerType === EDataType.sites);
	}, [markerType]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.mapSettings : null);
	}, [isPinned, pinFilter]);

	useEffect(() => {
		if (isClusterZoomLvlFromLocalStorage) setIsDefaultClusterZoom(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isDefaultClusterZoom) {
			setSelectedClusterZoomOption(DEFAULT_CLUSTER_ZOOM_OPTION);
		}
	}, [isDefaultClusterZoom]);

	useEffect(() => {
		if (!isDefaultClusterZoom && clusterZoomLvl === DEFAULT_CLUSTER_ZOOM_LVL) {
			setIsDefaultClusterZoom(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const isBoroughsStateChanged = areBoroughsEnabledLocalState !== areBoroughsEnabled;
		const isClusterZoomChanged = selectedClusterZoomOption?.value !== clusterZoomLvl;
		const isShowSiteIdsChanged = isShowSiteIdsLocalState !== isShowSiteIds;
		const isShowCellIdsChanged = isShowCellIdsLocalState !== isCellIdsVisible;

		setIsApplyAvailable(
			isBoroughsStateChanged ||
			isClusterZoomChanged ||
			isShowSiteIdsChanged ||
			isShowCellIdsChanged,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		areBoroughsEnabledLocalState,
		isDefaultClusterZoom,
		isShowCellIdsLocalState,
		isShowSiteIdsLocalState,
		selectedClusterZoomOption,
	]);

	const handleChangeBoroughs = useCallback(() => {
		setAreBoroughsEnabledLocalState(!areBoroughsEnabledLocalState);
	}, [areBoroughsEnabledLocalState]);

	const handleChangeShowSiteIds = useCallback(() => {
		setIsShowSiteIdsLocalState(!isShowSiteIdsLocalState);
	}, [isShowSiteIdsLocalState]);

	const handleChangeShowCellIds = useCallback(() => {
		setIsShowCellIdsLocalState(!isShowCellIdsLocalState);
	}, [isShowCellIdsLocalState]);

	const handleChangeDefaultClusterState = useCallback(() => {
		setIsDefaultClusterZoom(!isDefaultClusterZoom);
	}, [isDefaultClusterZoom]);

	const handleChangeClusterZoomLvl = useCallback((option: TSelectOption) => {
		setSelectedClusterZoomOption(option);
	}, []);

	const handleApply = useCallback(async () => {
		try {
			const { clusterZoomLevel } = await handleSetOrganisationMapSettings({
				clusterZoomLevel: selectedClusterZoomOption
					? Number(selectedClusterZoomOption.value)
					: DEFAULT_CLUSTER_ZOOM_LVL,
				organizationId,
			});

			dispatch(setClusterZoomLevel(clusterZoomLevel));
			dispatch(setBoroughsState(areBoroughsEnabledLocalState));
			dispatch(resetRightSideControls());
			dispatch(setIsShowSiteIds(isShowSiteIdsLocalState));
			dispatch(setCellIdsVisibility(isShowCellIdsLocalState));

			if (!isPinned) dispatch(resetRightSideControls());
		} catch (error) {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		}
	}, [
		areBoroughsEnabledLocalState,
		dispatch,
		handleSetOrganisationMapSettings,
		isPinned,
		isShowCellIdsLocalState,
		isShowSiteIdsLocalState,
		selectedClusterZoomOption,
		organizationId,
	]);

	const handleReset = useCallback(() => {
		dispatch(setBoroughsState(false));
		dispatch(resetClusterZoomLvl());
		dispatch(setIsShowSiteIds(false));

		if (!isPinned) dispatch(resetRightSideControls());
	}, [dispatch, isPinned]);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	const disabledBoroughsTooltip = showingCustomPolygonIds.length || isSectorsShown
		? 'To make boroughs available, turn off polygons/sectors'
		: undefined;

	return (
		<div
			ref={containerRef}
			className={cn(controlStyles.controlItemContainer, controlStyles.overflowVisible)}
		>
			<div className={styles.pinIconContainer}>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			<Checkbox
				checked={areBoroughsEnabledLocalState}
				disabled={!!showingCustomPolygonIds.length || isSectorsShown}
				disabledTooltip={disabledBoroughsTooltip}
				label='London boroughs'
				name='boroughs'
				onChange={handleChangeBoroughs}
			/>
			<div className={styles.manageZoom}>
				<div className={styles.sectionTitle}>Zoom level</div>
				<div className={styles.clusterZoomLvl}>
					Cluster zoom level
					<Tooltip content={tooltipContent}>
						<span className={commonStyles.infoIcon}>
							<DynamicIconWrapper icon={Icons.info} />
						</span>
					</Tooltip>
					<Select
						disabled={isDefaultClusterZoom || !isClusterZoomLvlAvailable}
						handleChange={handleChangeClusterZoomLvl}
						isWithoutUseForms
						name='clusterZoomLevel'
						options={zoomLevelsOptions}
						value={selectedClusterZoomOption}
					/>
				</div>
				<Checkbox
					checked={isDefaultClusterZoom}
					label='Default cluster zoom level'
					name='defaultClusterZoomLevel'
					onChange={handleChangeDefaultClusterState}
				/>
			</div>
			{isSitesMode && (
				<>
					<div className={styles.sectionTitle}>Site options</div>
					<Checkbox
						checked={isShowSiteIdsLocalState}
						label='Show Site IDs'
						name='showSiteIds'
						onChange={handleChangeShowSiteIds}
					/>
					<Checkbox
						checked={isShowCellIdsLocalState}
						label='Show Cell IDs on Sectors'
						name='showCellIdsOnSectors'
						onChange={handleChangeShowCellIds}
					/>
				</>
			)}
			<SubmissionButtons
				disabledPositive={!isApplyAvailable}
				negativeHandler={handleReset}
				negativeLabel='Reset all'
				positiveHandler={handleApply}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default memo(MapSettings);
